import { useState } from "react";
import { Vendor } from "@/data/mockData";
import { useProcurement } from "@/context/ProcurementContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { 
  Building2, 
  Edit, 
  Star,
  Phone,
  Mail,
  MapPin,
  Globe,
  User,
  CreditCard,
  Award,
  Package,
  TrendingUp,
  Clock,
  CheckCircle,
  Calendar,
  FileText,
  BarChart3
} from "lucide-react";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from "recharts";

interface VendorDetailsProps {
  vendorId: number;
  onClose: () => void;
  onEdit: (id: number) => void;
}

export const VendorDetails = ({ vendorId, onClose, onEdit }: VendorDetailsProps) => {
  const { state } = useProcurement();
  const [activeTab, setActiveTab] = useState("overview");

  const vendor = state.vendors.find(v => v.id === vendorId);

  if (!vendor) {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Vendor Not Found</DialogTitle>
            <DialogDescription>The requested vendor could not be found.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end">
            <Button onClick={onClose}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Calculate performance metrics
  const performanceData = [
    { subject: 'Quality', A: vendor.deliveryMetrics?.qualityScore || 0, fullMark: 5 },
    { subject: 'Delivery', A: vendor.performanceRating || 0, fullMark: 5 },
    { subject: 'Response', A: Math.max(0, 5 - ((vendor.deliveryMetrics?.responseTime || 0) / 12)), fullMark: 5 },
    { subject: 'Reliability', A: (vendor.deliveryMetrics?.totalOrders || 0) > 0 ? ((vendor.deliveryMetrics?.onTimeDeliveries || 0) / (vendor.deliveryMetrics?.totalOrders || 1)) * 5 : 0, fullMark: 5 },
    { subject: 'Communication', A: (vendor.performanceRating || 0) * 0.9, fullMark: 5 }
  ];

  // Get recent orders from this vendor
  const recentOrders = state.purchaseOrders
    .filter(po => po.vendorId === vendor.id)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5);

  // Get linked SKUs
  const linkedSKUs = state.skus.filter(sku => (vendor.linkedSKUs || []).includes(sku.id));

  // Performance trend data (mock data for demonstration)
  const performanceTrend = [
    { month: 'Jan', rating: 3.8 },
    { month: 'Feb', rating: 4.0 },
    { month: 'Mar', rating: 4.1 },
    { month: 'Apr', rating: 4.2 },
    { month: 'May', rating: 4.3 },
    { month: 'Jun', rating: vendor.performanceRating }
  ];

  const onTimePercentage = (vendor.deliveryMetrics?.totalOrders || 0) > 0 
    ? Math.round(((vendor.deliveryMetrics?.onTimeDeliveries || 0) / (vendor.deliveryMetrics?.totalOrders || 1)) * 100)
    : 0;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                {vendor.name}
              </DialogTitle>
              <DialogDescription className="flex items-center gap-4 mt-2">
                <span>{vendor.vendorCode}</span>
                <Badge variant={vendor.isActive ? "default" : "secondary"}>
                  {vendor.isActive ? "Active" : "Inactive"}
                </Badge>
                <div className="flex items-center gap-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`h-4 w-4 ${
                        i < Math.floor(vendor.performanceRating) 
                          ? 'text-yellow-400 fill-current' 
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                  <span className="ml-1 text-sm font-semibold">{(vendor.performanceRating || 0).toFixed(1)}</span>
                </div>
              </DialogDescription>
            </div>
            <Button onClick={() => onEdit(vendor.id)} className="ml-4">
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="contact">Contact</TabsTrigger>
            <TabsTrigger value="performance">Performance</TabsTrigger>
            <TabsTrigger value="orders">Orders</TabsTrigger>
            <TabsTrigger value="products">Products</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Key Metrics */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <BarChart3 className="h-4 w-4" />
                    Key Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-sm">
                        <span>Performance Rating</span>
                        <span className="font-medium">{(vendor.performanceRating || 0).toFixed(1)}/5.0</span>
                      </div>
                      <Progress value={((vendor.performanceRating || 0) / 5) * 100} className="h-2 mt-1" />
                    </div>
                    
                    <div>
                      <div className="flex justify-between text-sm">
                        <span>On-Time Delivery</span>
                        <span className="font-medium">{onTimePercentage}%</span>
                      </div>
                      <Progress value={onTimePercentage} className="h-2 mt-1" />
                    </div>
                    
                    <div>
                      <div className="flex justify-between text-sm">
                        <span>Quality Score</span>
                        <span className="font-medium">{(vendor.deliveryMetrics?.qualityScore || 0).toFixed(1)}/5.0</span>
                      </div>
                      <Progress value={((vendor.deliveryMetrics?.qualityScore || 0) / 5) * 100} className="h-2 mt-1" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Order Statistics */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Package className="h-4 w-4" />
                    Order Statistics
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-muted-foreground">Total Orders</p>
                      <p className="text-2xl font-bold">{vendor.deliveryMetrics.totalOrders}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">On-Time</p>
                      <p className="text-2xl font-bold">{vendor.deliveryMetrics.onTimeDeliveries}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Response Time</p>
                      <p className="text-lg font-semibold">{vendor.deliveryMetrics.responseTime}h</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Products</p>
                      <p className="text-lg font-semibold">{vendor.linkedSKUs.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Payment Terms */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <CreditCard className="h-4 w-4" />
                    Financial Info
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Payment Terms:</span>
                      <span className="font-medium">{vendor.paymentTerms}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Tax ID:</span>
                      <span className="font-medium">{vendor.taxInfo.taxId}</span>
                    </div>
                    {vendor.taxInfo.gstNumber && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">GST Number:</span>
                        <span className="font-medium">{vendor.taxInfo.gstNumber}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Certifications */}
            {vendor.certifications.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="h-5 w-5" />
                    Certifications & Standards
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {vendor.certifications.map((cert, index) => (
                      <Badge key={index} variant="outline" className="text-sm">
                        {cert}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      Last order delivered
                    </span>
                    <span className="text-muted-foreground">2 days ago</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      Quality inspection passed
                    </span>
                    <span className="text-muted-foreground">5 days ago</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                      Price update received
                    </span>
                    <span className="text-muted-foreground">1 week ago</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contact Tab */}
          <TabsContent value="contact" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Company Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{vendor.contactDetails.email}</p>
                        <p className="text-sm text-muted-foreground">Main Email</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{vendor.contactDetails.phone}</p>
                        <p className="text-sm text-muted-foreground">Main Phone</p>
                      </div>
                    </div>
                    
                    {vendor.contactDetails.website && (
                      <div className="flex items-center gap-3">
                        <Globe className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <a 
                            href={vendor.contactDetails.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="font-medium text-primary hover:underline"
                          >
                            {vendor.contactDetails.website}
                          </a>
                          <p className="text-sm text-muted-foreground">Website</p>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-start gap-3">
                      <MapPin className="h-4 w-4 text-muted-foreground mt-1" />
                      <div>
                        <p className="font-medium">
                          {vendor.contactDetails.address.street}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {vendor.contactDetails.address.city}, {vendor.contactDetails.address.state} {vendor.contactDetails.address.zip}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {vendor.contactDetails.address.country}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Primary Contact</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <User className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{vendor.contactDetails.primaryContact.name}</p>
                      <p className="text-sm text-muted-foreground">{vendor.contactDetails.primaryContact.title}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3 pl-15">
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{vendor.contactDetails.primaryContact.email}</span>
                    </div>
                    
                    {vendor.contactDetails.primaryContact.phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{vendor.contactDetails.primaryContact.phone}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Performance Radar</CardTitle>
                  <CardDescription>Multi-dimensional performance analysis</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={performanceData}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="subject" />
                        <PolarRadiusAxis angle={90} domain={[0, 5]} />
                        <Radar
                          name="Performance"
                          dataKey="A"
                          stroke="#8884d8"
                          fill="#8884d8"
                          fillOpacity={0.3}
                        />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Performance Trend</CardTitle>
                  <CardDescription>6-month performance rating trend</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={performanceTrend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis domain={[0, 5]} />
                        <Tooltip />
                        <Line 
                          type="monotone" 
                          dataKey="rating" 
                          stroke="#8884d8" 
                          strokeWidth={2}
                          dot={{ fill: '#8884d8' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <div>
                      <p className="text-sm text-muted-foreground">Delivery Rate</p>
                      <p className="text-lg font-bold">{onTimePercentage}%</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4 text-yellow-500" />
                    <div>
                      <p className="text-sm text-muted-foreground">Quality Score</p>
                      <p className="text-lg font-bold">{(vendor.deliveryMetrics?.qualityScore || 0).toFixed(1)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-blue-500" />
                    <div>
                      <p className="text-sm text-muted-foreground">Response Time</p>
                      <p className="text-lg font-bold">{vendor.deliveryMetrics.responseTime}h</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-green-500" />
                    <div>
                      <p className="text-sm text-muted-foreground">Overall Rating</p>
                      <p className="text-lg font-bold">{(vendor.performanceRating || 0).toFixed(1)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Recent Purchase Orders</CardTitle>
                <CardDescription>Latest orders from this vendor</CardDescription>
              </CardHeader>
              <CardContent>
                {recentOrders.length > 0 ? (
                  <div className="space-y-4">
                    {recentOrders.map((order) => (
                      <div key={order.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <div className="font-medium">{order.poNumber}</div>
                          <div className="text-sm text-muted-foreground">
                            {order.items.length} items • ${order.totalAmount.toLocaleString()}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(order.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                        <Badge variant={
                          order.status === 'FULLY_RECEIVED' ? 'default' :
                          order.status === 'CREATED' ? 'secondary' :
                          'outline'
                        }>
                          {order.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No purchase orders found</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Linked Products</CardTitle>
                <CardDescription>SKUs available from this vendor</CardDescription>
              </CardHeader>
              <CardContent>
                {linkedSKUs.length > 0 ? (
                  <div className="space-y-4">
                    {linkedSKUs.map((sku) => {
                      const vendorLink = sku.linkedVendors.find(link => link.vendorId === vendor.id);
                      return (
                        <div key={sku.id} className="flex items-center justify-between p-4 border rounded-lg">
                          <div>
                            <div className="font-medium">{sku.name}</div>
                            <div className="text-sm text-muted-foreground">{sku.skuCode}</div>
                            <Badge variant="outline" className="text-xs mt-1">{sku.category}</Badge>
                          </div>
                          {vendorLink && (
                            <div className="text-right">
                              <div className="font-semibold">${vendorLink.defaultPrice.toFixed(2)}</div>
                              <div className="text-sm text-muted-foreground">
                                {vendorLink.leadTime} days lead time
                              </div>
                              <div className="text-xs text-muted-foreground">
                                Min. order: {vendorLink.minimumOrderQty}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No products linked to this vendor</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
